import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { hasBlocks } from '../labEngine';
import { computeGSA, COARSE_LABELS, FINE_LABELS } from './gsaCalc';
import './GSATest.css';

const n = (s) => (s === '' || s == null ? NaN : Number(s));
const f1 = (x) => (Number.isFinite(x) ? x.toFixed(1) : '—');

// Grain size analysis (sieve analysis) — IS 2720 (Part 4):1985.
// Stage 1: whole dry sample on 100 / 75 / 19 / 4.75 mm.  Stage 2: a sub-sample passing 4.75 mm on 2.00 mm / 425 µ / 75 µ.
// Project details live in the portal; `material` decides whether dry sieving is allowed (not for clay).
export default function GSATest({ material, onReport }) {
  const hasClay = material === 'clay';
  const [f, setF] = useState({
    totalDry: '', wet: '', coarse: ['', '', '', ''], method: 'wet', partialMass: '', fine: ['', '', ''], pan: '',
  });
  const method = hasClay ? 'wet' : f.method; // clay is always wet-sieved
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setArr = (k, i) => (e) => setF((s) => ({ ...s, [k]: s[k].map((v, j) => (j === i ? e.target.value : v)) }));

  // Pan is not required to start calculating: if it is missing for dry sieving the engine says so in the list below.
  const fields = [
    ['total dry sample', f.totalDry], ...f.coarse.map((v, i) => [`retained on ${COARSE_LABELS[i]}`, v]),
    ['sub-sample weight', f.partialMass], ...f.fine.map((v, i) => [`retained on ${FINE_LABELS[i]}`, v]),
  ];
  const missing = fields.filter(([, v]) => !(Number.isFinite(n(v)) && n(v) >= 0)).map(([l]) => l);
  const complete = missing.length === 0;
  const dirty = [f.totalDry, f.wet, f.partialMass, f.pan, ...f.coarse, ...f.fine].some((v) => v !== '');

  const inputs = useMemo(() => complete ? {
    totalDryG: n(f.totalDry),
    wetG: f.wet === '' ? null : n(f.wet),
    coarseRetainedG: f.coarse.map(n),
    partial: { massG: n(f.partialMass), retainedG: f.fine.map(n), method, ...(method === 'dry' ? { panG: n(f.pan) } : {}) },
    hasClay,
  } : null, [complete, f, method, hasClay]);

  const { result, issues } = useMemo(() => (inputs ? computeGSA(inputs) : { result: null, issues: [] }), [inputs]);
  const ready = !!result && !hasBlocks(issues);

  // a plain string, so the report below only changes when something really changed
  const hint = ready ? '' : !dirty ? 'enter the readings'
    : missing.length ? `fill ${missing[0]}${missing.length > 1 ? ` (+${missing.length - 1} more)` : ''}`
      : (issues.find((i) => i.level === 'block')?.msg || '');

  const report = useMemo(() => ({
    status: ready ? 'ready' : dirty ? 'incomplete' : 'empty',
    ready, dirty,
    summary: ready ? `Gravel ${f1(result.gravelPct)} · Sand ${f1(result.sandPct)} · Fines ${f1(result.finesPct)}` : '',
    hint,
    payload: ready ? { inputs, result, issues } : null,
  }), [ready, dirty, hint, result, issues, inputs]);

  const cb = useRef(onReport); cb.current = onReport;
  useEffect(() => { cb.current?.(report); }, [report]);

  const pass = (size) => result?.rows.find((r) => r.size === size)?.pctPassing;
  // plain render helpers (not components) so inputs keep focus while typing
  const row = ({ label, size, value, onChange }) => (
    <div className="gsa-row">
      <span className="gsa-sieve">{label}</span>
      <input className="gsa-input" type="number" step="0.01" min="0" inputMode="decimal" aria-label={`Retained on ${label}`}
        value={value} onChange={onChange} />
      <span className="gsa-calc">{result ? f1(100 - pass(size)) : '—'}</span>
      <span className="gsa-calc gsa-pass">{result ? f1(pass(size)) : '—'}</span>
    </div>
  );
  const head = (
    <div className="gsa-row gsa-head"><span>IS sieve</span><span>Retained (g)</span><span>Cum. retained %</span><span>Passing %</span></div>
  );
  const nmcShown = f.wet !== '' && f.totalDry !== '' && n(f.wet) >= n(f.totalDry) && n(f.totalDry) > 0
    ? ((n(f.wet) - n(f.totalDry)) / n(f.totalDry)) * 100 : null;

  return (
    <div className="gsa">
      <section>
        <div className="gsa-sec-head"><h4>Total sample</h4><span className="gsa-muted">oven-dried weight of the whole sample</span></div>
        <div className="gsa-two">
          <label className="gsa-field">Weight of total dry sample (g) *
            <input className="gsa-input" type="number" step="0.01" min="0" value={f.totalDry} onChange={set('totalDry')} /></label>
          <label className="gsa-field">Weight of wet soil (g) <span className="gsa-opt">optional</span>
            <input className="gsa-input" type="number" step="0.01" min="0" value={f.wet} onChange={set('wet')} /></label>
        </div>
        {nmcShown !== null && (
          <p className="gsa-muted gsa-nmc">Water {(n(f.wet) - n(f.totalDry)).toFixed(2)} g · natural moisture content {nmcShown.toFixed(1)} %</p>
        )}
      </section>

      <section>
        <div className="gsa-sec-head"><h4>Stage 1 — whole sample on coarse sieves</h4><span className="gsa-muted">enter 0 if nothing is retained</span></div>
        <div className="gsa-table">
          {head}
          {COARSE_LABELS.map((l, i) => (
            <Fragment key={l}>{row({ label: l, size: [100, 75, 19, 4.75][i], value: f.coarse[i], onChange: setArr('coarse', i) })}</Fragment>
          ))}
        </div>
      </section>

      <section>
        <div className="gsa-sec-head"><h4>Stage 2 — sub-sample passing 4.75 mm</h4></div>
        <div className="gsa-two">
          <label className="gsa-field">Sieving method
            <select className="gsa-input" value={method} onChange={set('method')}>
              <option value="wet">Wet sieving</option>
              <option value="dry" disabled={hasClay}>Dry sieving</option>
            </select>
            {hasClay && <span className="gsa-muted">Clay is always wet-sieved (IS 2720-4, 4.3.2).</span>}
          </label>
          <label className="gsa-field">Weight of sub-sample (g) *
            <input className="gsa-input" type="number" step="0.01" min="0" value={f.partialMass} onChange={set('partialMass')} /></label>
        </div>
        <div className="gsa-table">
          {head}
          {FINE_LABELS.map((l, i) => (
            <Fragment key={l}>{row({ label: l, size: [2, 0.425, 0.075][i], value: f.fine[i], onChange: setArr('fine', i) })}</Fragment>
          ))}
        </div>
        {method === 'dry' && (
          <label className="gsa-field gsa-pan">Pan (g) * — checked against the sub-sample weight
            <input className="gsa-input" type="number" step="0.01" min="0" value={f.pan} onChange={set('pan')} /></label>
        )}
        <p className="gsa-muted gsa-note">Fine-sieve weights are scaled up to the whole sample in the report.</p>
      </section>

      {dirty && missing.length > 0 && (
        <p className="gsa-missing"><b>Still to fill:</b> {missing.join(', ')}</p>
      )}

      {result && (
        <section className="gsa-result">
          <div className="gsa-stat"><span>Cobble</span><b>{f1(result.cobblePct)}</b></div>
          <div className="gsa-stat"><span>Gravel</span><b>{f1(result.gravelPct)}</b></div>
          <div className="gsa-stat"><span>Sand</span><b>{f1(result.sandPct)}</b>
            <small>C {f1(result.coarseSandPct)} · M {f1(result.mediumSandPct)} · F {f1(result.fineSandPct)}</small></div>
          <div className="gsa-stat"><span>Silt &amp; clay</span><b>{f1(result.finesPct)}</b></div>
        </section>
      )}

      {issues.length > 0 && (
        <ul className="gsa-issues">
          {issues.map((it, i) => (
            <li key={i} className={it.level === 'block' ? 'gsa-err' : 'gsa-warn'}>
              <b>{it.level === 'block' ? 'Fix' : 'Check'}</b> {it.msg} <span className="gsa-muted">({it.clause})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}